/**
 * Deterministic pseudo-random numbers. The environment is rolled from a seed stored in the map,
 * so two players racing the same map get the same weather, and a replay of a race reproduces it
 * exactly. `Math.random` cannot do either, so it is never used in `src/sim`.
 *
 * mulberry32 is a 32-bit generator: one multiply-xor round, a period of 2^32, and quality far
 * beyond what wind gusts need. It is chosen for being short enough to read and verify here.
 */

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Folds several numbers into one seed. Every roll derives its own stream this way — the seed
 * mixed with the turn number and a label — so adding a rolled value later does not shift the
 * numbers every other roll already produces.
 */
export function hashSeed(...parts: readonly (number | string)[]): number {
  let hash = 0x811c9dc5;

  for (const part of parts) {
    const text = typeof part === 'number' ? part.toString(36) : part;

    for (let i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
  }

  return hash >>> 0;
}

export function randomBetween(rng: Rng, min: number, max: number): number {
  return min + rng() * (max - min);
}
